import Lenis from "lenis";
import { getScope, onMount, signal, type ReadonlySignal } from "@zyra/core";

export interface SmoothScrollOptions {
  /** Interpolation duration in seconds. */
  duration?: number;
  /** Interpolation factor; takes precedence over duration when supplied. */
  lerp?: number;
  anchors?: boolean;
}
export interface SmoothScrollController {
  readonly enabled: ReadonlySignal<boolean>;
  scrollTo(
    target: number | HTMLElement,
    options?: { immediate?: boolean; offset?: number },
  ): void;
  destroy(): void;
}
let active:
  | {
      engine?: Lenis;
      enabled: ReturnType<typeof signal<boolean>>;
      references: number;
      dispose(): void;
    }
  | undefined;

/** One shared window scroller. The first active owner supplies its configuration. */
export function createSmoothScroll(
  options: SmoothScrollOptions = {},
): SmoothScrollController {
  if (
    options.duration !== undefined &&
    (!Number.isFinite(options.duration) || options.duration <= 0)
  )
    throw new Error("Smooth scroll duration must be positive");
  if (
    options.lerp !== undefined &&
    (!Number.isFinite(options.lerp) || options.lerp <= 0 || options.lerp > 1)
  )
    throw new Error("Smooth scroll lerp must be in (0, 1]");
  if (typeof window === "undefined")
    throw new Error(
      "createSmoothScroll requires a browser; use useSmoothScroll during component setup",
    );
  if (!active) {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const state = {
      engine: undefined as Lenis | undefined,
      enabled: signal(false),
      references: 0,
      dispose: () => {},
    };
    const configure = () => {
      state.engine?.destroy();
      state.engine = preference.matches
        ? undefined
        : new Lenis({
            autoRaf: true,
            anchors: options.anchors ?? true,
            smoothWheel: true,
            ...(options.lerp === undefined
              ? { duration: options.duration ?? 0.85 }
              : { lerp: options.lerp }),
            prevent: (element) =>
              !!document.querySelector('dialog[open], [aria-modal="true"]') ||
              element.hasAttribute("data-zyra-scroll-prevent") ||
              element.tagName === "DIALOG",
          });
      state.enabled.value = !!state.engine;
    };
    configure();
    preference.addEventListener("change", configure);
    state.dispose = () => {
      preference.removeEventListener("change", configure);
      state.engine?.destroy();
      state.enabled.value = false;
    };
    active = state;
  }
  const state = active;
  state.references++;
  let disposed = false;
  const controller: SmoothScrollController = {
    enabled: state.enabled,
    scrollTo(target, settings = {}) {
      if (disposed) return;
      if (state.engine) state.engine.scrollTo(target, settings);
      else
        window.scrollTo({
          top:
            (typeof target === "number"
              ? target
              : target.getBoundingClientRect().top + window.scrollY) +
            (settings.offset ?? 0),
          behavior: "instant",
        });
    },
    destroy() {
      if (disposed) return;
      disposed = true;
      if (--state.references === 0) {
        state.dispose();
        if (active === state) active = undefined;
      }
    },
  };
  getScope()?.add(controller.destroy);
  return controller;
}

/** Initialize smooth scrolling after mount and release it with the component scope. */
export function useSmoothScroll(options: SmoothScrollOptions = {}): void {
  onMount(() => createSmoothScroll(options).destroy);
}
